<template>
  <div
    v-if="!groupsStore.groupData"
    class="mx-auto min-h-screen overflow-hidden pb-24"
  >
    <div
      class="m-4 h-32 animate-pulse rounded bg-gray-300 dark:bg-kekkle-dark-light"
    ></div>
    <div
      class="m-4 h-16 animate-pulse rounded bg-gray-300 dark:bg-kekkle-dark-light"
    ></div>
    <div
      class="m-4 h-32 animate-pulse rounded bg-gray-300 dark:bg-kekkle-dark-light"
    ></div>
    <div
      class="m-4 h-16 animate-pulse rounded bg-gray-300 dark:bg-kekkle-dark-light"
    ></div>
    <div
      class="m-4 h-32 animate-pulse rounded bg-gray-300 dark:bg-kekkle-dark-light"
    ></div>
  </div>
  <div v-else class="container mx-auto min-h-screen pb-24">
    <div class="h-12"></div>
    <div
      class="fixed top-0 z-40 flex h-12 w-full max-w-xl flex-row items-center justify-between bg-white px-2 py-2 dark:bg-kekkle-dark-dark"
    >
      <p
        translate="no"
        class="flex transform flex-row items-center whitespace-nowrap font-medium duration-300 dark:text-slate-200"
        @click.stop="openGroupSelectorBottomSheet"
      >
        {{
          groupsStore.groupData.name.length > 50
            ? groupsStore.groupData.name.slice(0, 50) + "..."
            : groupsStore.groupData.name
        }}
        <chevron-down-icon
          class="max-h-8 h-8 w-full transform p-2 duration-300 hover:rotate-45 dark:text-slate-200"
        ></chevron-down-icon>
      </p>
      <div class="flex flex-row items-center gap-2">
        <div class="relative"></div>
        <div v-if="!pwaInstalled && hasInstallPrompt" class="relative">
          <download-icon
            class="max-h-8 h-8 transform animate-pulse dark:text-slate-200"
            @click="installPrompt.prompt()"
          >
          </download-icon>
          <span class="absolute right-0 top-0 flex h-3 w-3">
            <span
              class="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange opacity-75"
            ></span>
            <span
              class="relative inline-flex h-3 w-3 rounded-full bg-orange"
            ></span>
          </span>
        </div>

        <div
          class="relative cursor-pointer"
          @click="openNotificationsBottomSheet"
        >
          <bell-icon
            class="max-h-8 h-8 transform duration-300 hover:scale-105 dark:text-slate-200"
          ></bell-icon>
          <span
            v-if="inAppNotificationsStore.unreadCount > 0"
            class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-kekkle-red px-1 text-[10px] font-bold text-white shadow"
          >
            {{
              inAppNotificationsStore.unreadCount > 99
                ? "99+"
                : inAppNotificationsStore.unreadCount
            }}
          </span>
        </div>

        <cog-icon
          class="max-h-8 h-8 transform duration-300 hover:rotate-45 dark:text-slate-200"
          @click="$router.push({ name: 'settings' })"
        ></cog-icon>
      </div>
    </div>
    <group-header-thumb></group-header-thumb>
    <div
      class="mt-2 grid grid-cols-5 items-center justify-items-center p-2 text-center"
    >
      <div class="max-h-20">
        <p class="text-xl font-bold dark:text-slate-200">
          {{ groupsStore.groupData.general_data.number_of_members }}
        </p>
        <p class="text-sm capitalize dark:text-slate-200">
          🧑‍🤝‍🧑
          {{
            $t(
              "global.member",
              groupsStore.groupData.general_data.number_of_members,
            )
          }}
        </p>
      </div>
      <div class="max-h-20">
        <p
          class="cursor-pointer text-xl font-bold dark:text-slate-200"
          @click="$router.push({ name: 'Quotes' })"
        >
          {{ groupsStore.groupData.general_data.number_of_quotes }}
        </p>
        <p class="text-sm capitalize dark:text-slate-200">
          📢
          {{
            $t(
              "global.quote",
              groupsStore.groupData.general_data.number_of_quotes,
            )
          }}
        </p>
      </div>
      <div class="max-h-20">
        <p
          class="cursor-pointer text-xl font-bold dark:text-slate-200"
          @click="$router.push({ name: 'Agenda' })"
        >
          {{ groupsStore.groupData.general_data.number_of_events }}
        </p>
        <p class="text-sm capitalize dark:text-slate-200">
          📅
          {{
            $t(
              "global.event",
              groupsStore.groupData.general_data.number_of_events,
            )
          }}
        </p>
      </div>
      <div class="max-h-20">
        <p
          class="cursor-pointer text-xl font-bold capitalize dark:text-slate-200"
          @click="$router.push({ name: 'Photos' })"
        >
          {{ groupsStore.groupData.general_data.number_of_photos ?? 0 }}
        </p>
        <p class="text-sm dark:text-slate-200">
          📸
          {{
            $t(
              "global.photos",
              groupsStore.groupData.general_data.number_of_photos ?? 0,
            )
          }}
        </p>
      </div>
      <div class="max-h-20">
        <p
          class="cursor-pointer text-xl font-bold capitalize dark:text-slate-200"
          @click="$router.push({ name: 'Shorts' })"
        >
          {{
            groupsStore.groupData.general_data.number_of_shorts ??
            shortsStore.shorts.length ??
            0
          }}
        </p>
        <p class="text-sm dark:text-slate-200">
          🎬
          {{
            $t(
              "global.shorts",
              groupsStore.groupData.general_data.number_of_shorts ??
                shortsStore.shorts.length ??
                0,
            )
          }}
        </p>
      </div>
    </div>

    <!-- Active Dilemma Quick Notification Banner -->
    <div
      v-if="dilemmasStore.nextUnansweredDilemma"
      class="cursor-pointer p-2"
      @click="$router.push({ name: 'Dilemmas' })"
    >
      <information-text color="yellow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs font-bold text-kekkle-dark dark:text-slate-900">
              🤔
              {{ $t("pages.dilemmas.unanswered_title", "Unanswered Dilemma!") }}
            </p>
            <p
              class="mt-0.5 text-sm font-medium text-kekkle-dark dark:text-slate-900"
            >
              {{ dilemmasStore.nextUnansweredDilemma.question }}
            </p>
          </div>
          <span
            class="ml-2 text-xs font-bold text-kekkle-dark hover:underline dark:text-slate-900"
          >
            {{ $t("pages.dilemmas.tap_to_vote", "Tap to vote") }} 👉
          </span>
        </div>
      </information-text>
    </div>

    <!-- Active Shorts Quick Banner -->
    <div
      v-if="shortsStore.hasUnviewedShorts"
      class="cursor-pointer p-2"
      @click="$router.push({ name: 'Shorts' })"
    >
      <information-text color="yellow">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-xl">🎬</span>
            <div>
              <p class="text-xs font-bold text-kekkle-dark dark:text-slate-900">
                {{ $t("pages.shorts.title") }} ({{ shortsStore.unviewedCount }})
              </p>
              <p
                class="text-xs font-medium text-kekkle-dark/80 dark:text-slate-900/80"
              >
                {{ $t("pages.shorts.watch_group_shorts") }}
              </p>
            </div>
          </div>
          <span
            class="ml-2 text-xs font-bold text-kekkle-dark hover:underline dark:text-slate-900"
          >
            {{ $t("pages.shorts.watch_now") }} 👉
          </span>
        </div>
      </information-text>
    </div>
    <!-- Quote Memories Section -->
    <div class="p-2">
      <h1 class="mb-3 font-medium dark:text-slate-200">
        🕰️ {{ $t("pages.group.memories") }}:
      </h1>
      <div class="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <!-- Active memories -->
        <div
          v-for="memory in quoteMemories"
          :key="memory.yearsAgo"
          class="min-w-fit cursor-pointer rounded-lg border-2 border-kekkle-dark-dark bg-white p-3 shadow-purple-hard-sm hover:shadow-none dark:bg-kekkle-dark-light dark:hover:shadow-none"
          @click="navigateToMemory(memory.date)"
        >
          <div class="text-center">
            <div class="text-2xl">📜</div>
            <div class="text-sm font-bold dark:text-slate-200">
              {{ memory.yearsAgo }}
              {{
                memory.yearsAgo === 1
                  ? $t("global.time.year_ago")
                  : $t("global.time.years_ago")
              }}
            </div>
            <div class="text-xs dark:text-slate-200">
              {{ memory.count }}
              {{
                memory.count === 1 ? $t("global.quote") : $t("global.quotes")
              }}
            </div>
          </div>
        </div>
        <div
          v-for="memory in photoMemories"
          :key="memory.yearsAgo"
          class="min-w-fit cursor-pointer rounded-lg border-2 border-kekkle-dark-dark bg-white p-3 shadow-purple-hard-sm hover:shadow-none dark:bg-kekkle-dark-light dark:hover:shadow-none"
          @click="navigateToPhotoMemory(memory.date)"
        >
          <div class="text-center">
            <div class="text-2xl">🖼️</div>
            <div class="text-sm font-bold dark:text-slate-200">
              {{ memory.yearsAgo }}
              {{
                memory.yearsAgo === 1
                  ? $t("global.time.year_ago")
                  : $t("global.time.years_ago")
              }}
            </div>
            <div class="text-xs dark:text-slate-200">
              {{ memory.count }}
              {{
                memory.count === 1 ? $t("global.photo") : $t("global.photos")
              }}
            </div>
          </div>
        </div>

        <!-- Placeholder when no memories exist -->
        <div
          v-if="quoteMemories.length === 0 && photoMemories.length === 0"
          class="min-w-fit cursor-not-allowed rounded-lg border-2 border-kekkle-dark-dark bg-gray-100 p-3 opacity-60 dark:bg-kekkle-dark-light"
        >
          <div class="text-center">
            <div
              class="text-sm font-medium text-kekkle-dark dark:text-slate-200"
            >
              {{ $t("pages.group.no_memories_for_today") }}
            </div>
            <div class="text-xs text-kekkle-dark dark:text-slate-200">
              {{ $t("pages.group.check_again_tomorrow") }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="groupsStore.groupData.description"
      class="p-2 dark:text-slate-200"
    >
      <h1 class="font-medium dark:text-slate-200">
        ✏️ {{ $t("pages.group.description") }}:
      </h1>
      <p class="">{{ groupsStore.groupData.description }}</p>
    </div>
    <!-- Group Selector Bottom Sheet -->
    <group-selector-bottom-sheet
      v-model:open="groupSelectorBottomSheetOpen"
    ></group-selector-bottom-sheet>

    <!-- Invite Members Bottom Sheet -->
    <invite-members-bottom-sheet
      v-model:open="inviteBottomSheetOpen"
    ></invite-members-bottom-sheet>

    <!-- Notifications Bottom Sheet -->
    <notifications-bottom-sheet
      v-model:open="notificationsBottomSheetOpen"
    ></notifications-bottom-sheet>

    <p class="p-2 font-medium dark:text-slate-200">
      🧑‍🤝‍🧑 {{ $t("pages.group.members") }}:
    </p>
    <div class="flex flex-wrap items-center p-2">
      <div
        v-for="member in groupsStore.activeGroupMembersSorted"
        :key="userStore.activeGroup + '/' + member.uuid"
        class="mx-2 w-16 text-center"
      >
        <small-user-thumb
          :uuid="member.uuid"
          :name="member.name ? member.name : ''"
        ></small-user-thumb>
        <p class="my-2 truncate text-sm font-medium dark:text-slate-200">
          {{
            member.uuid === authStore.authUuid
              ? $t("components.quote.you")
              : ""
          }}{{ member.name }}
        </p>
      </div>
      <div
        class="mx-2 flex-shrink-0 pb-10 text-center"
        @click="openInviteBottomSheet"
      >
        <!--Invite button-->
        <div
          :class="groupsStore.activeGroupMembersSorted.length < 2 ? '' : 'w-14'"
          class="flex h-14 items-center justify-center overflow-hidden rounded-lg border-2 border-black-dark bg-white px-2 shadow-blue-hard-sm dark:bg-kekkle-dark-light dark:shadow-teal-hard-sm"
        >
          <user-add-icon
            class="h-6 text-kekkle-blue-dark dark:text-slate-200"
          ></user-add-icon>
          <p
            v-if="groupsStore.activeGroupMembersSorted.length < 2"
            class="ml-2 font-medium text-kekkle-dark dark:text-white"
          >
            {{ $t("pages.invite.invite_button") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  ChevronDownIcon,
  DownloadIcon,
  CogIcon,
  BellIcon,
  UserAddIcon,
} from "@heroicons/vue/outline";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { usePromptsStore } from "@/stores/modules/prompts/prompts";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { useEventsStore } from "@/stores/modules/events/events";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useInAppNotificationsStore } from "@/stores/modules/notifications/in-app-notifications";
import { ImageSizes } from "@/enums/images-sizes";
import SmallUserThumb from "../components/SmallUserThumb.vue";
import { setErrorToast } from "@/helpers/notifications/toast";
import GroupHeaderThumb from "../components/GroupHeaderThumb.vue";
import { isSupported } from "firebase/messaging";
import { useNotificationsStore } from "@/stores/modules/notifications/notifications";
import { askFcmTokenFlutter } from "@/helpers/flutter/features/notifications";
import { isRunningInFlutter } from "@/helpers/flutter/capabilities";
import InviteMembersBottomSheet from "@/components/InviteMembersBottomSheet.vue";
import GroupSelectorBottomSheet from "@/components/GroupSelectorBottomSheet.vue";
import NotificationsBottomSheet from "@/components/NotificationsBottomSheet.vue";
import InformationText from "@/components/InformationText.vue";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";
import * as Sentry from "@sentry/vue";

export default {
  name: "GroupView",
  components: {
    InformationText,
    GroupSelectorBottomSheet,
    InviteMembersBottomSheet,
    NotificationsBottomSheet,
    GroupHeaderThumb,
    SmallUserThumb,
    CogIcon,
    BellIcon,
    ChevronDownIcon,
    DownloadIcon,
    UserAddIcon,
  },
  setup() {
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const promptsStore = usePromptsStore();
    const quotesStore = useQuotesStore();
    const photosStore = usePhotosStore();
    const eventsStore = useEventsStore();
    const dilemmasStore = useDilemmasStore();
    const shortsStore = useShortsStore();
    const notificationsStore = useNotificationsStore();
    const inAppNotificationsStore = useInAppNotificationsStore();
    const { maybePromptReview } = useAppReviewPrompt();
    const route = useRoute();
    const router = useRouter();

    watch(
      () => route.query.shortId,
      (targetShortId) => {
        if (targetShortId) {
          router.replace({ name: "Shorts", query: route.query });
        }
      },
      { immediate: true },
    );

    return {
      groupsStore,
      userStore,
      authStore,
      promptsStore,
      quotesStore,
      photosStore,
      eventsStore,
      dilemmasStore,
      shortsStore,
      notificationsStore,
      inAppNotificationsStore,
      maybePromptReview,
    };
  },
  data() {
    return {
      groupSelectorBottomSheetOpen: false,
      inviteBottomSheetOpen: false,
      notificationsBottomSheetOpen: false,
      pwaInstalled: true,
    };
  },
  computed: {
    hasInstallPrompt() {
      return this.promptsStore.pwaInstallPrompt !== null;
    },
    installPrompt() {
      return this.promptsStore.pwaInstallPrompt;
    },
    quoteMemories() {
      return this.quotesStore.quoteMemories;
    },
    memoriesLoaded() {
      return (
        this.quotesStore.quoteMemories.length > 0 ||
        this.quotesStore.allQuotes.length > 0
      );
    },
    photoMemories() {
      return this.photosStore.photoMemories;
    },
    photoMemoriesLoaded() {
      return (
        this.photosStore.photoMemories.length > 0 ||
        this.photosStore.allPhotos.length > 0
      );
    },
  },
  async created() {
    this.userStore.refreshUserData();
    this.groupsStore.refreshCurrentGroupData();
    window.scrollTo({ top: 0, left: 0 });
    this.pwaInstalled = !!(
      window.navigator.standalone ||
      window.matchMedia("(display-mode: standalone)").matches ||
      isRunningInFlutter()
    );

    //only fetch once per session.
    if (!this.notificationsStore.hasFetched) {
      //flutter
      if (isRunningInFlutter()) {
        askFcmTokenFlutter();
        this.notificationsStore.setHasFetched(true);
      }
      //not in flutter
      else {
        if (await isSupported()) {
          try {
            await Notification.requestPermission();
          } catch {
            //
          }
          try {
            await this.notificationsStore.registerMessageToken();
          } catch {
            //
          }
        }
      }
    }

    // Start listeners so memories and events are available
    this.quotesStore.subscribeToQuotes();
    this.photosStore.subscribeToPhotos();
    this.eventsStore.subscribeToEvents();
    this.dilemmasStore.subscribeToDilemmas();
  },
  methods: {
    openNotificationsBottomSheet() {
      this.notificationsBottomSheetOpen = true;
    },
    openGroupSelectorBottomSheet() {
      this.groupSelectorBottomSheetOpen = true;
    },
    openInviteBottomSheet() {
      this.inviteBottomSheetOpen = true;
    },
    async selectGroup(groupId) {
      this.notificationsStore.setHasFetched(false);
      // Note: Quote and photo re-subscription is handled automatically by store watchers on active group change
      try {
        await this.userStore.switchActiveGroup(groupId);
        await this.groupsStore.downloadGroupPictureData(ImageSizes.Thumb);
      } catch (e) {
        console.log(e);
        Sentry.captureException(e);
        setErrorToast({
          message: this.$t("pages.group.switch_group_failed"),
        });
      }
    },
    navigateToMemory(date) {
      this.maybePromptReview();
      // Format date as YYYY-MM-DD for the query parameter
      const formattedDate = date.toISOString().split("T")[0];
      this.$router.push({
        name: "Quotes",
        query: { date: formattedDate },
      });
    },
    navigateToPhotoMemory(date) {
      this.maybePromptReview();
      // Format date as YYYY-MM-DD for the query parameter
      const formattedDate = date.toISOString().split("T")[0];
      this.$router.push({
        name: "Photos",
        query: { date: formattedDate },
      });
    },
  },
};
</script>
